
import { Header } from '../../../components/feature/Header';
import { Footer } from '../../../components/feature/Footer';

export default function MetroDetroitForecast2025Page() {
  return (
    <div className="min-h-screen">
      <Header />
      
      <main className="pt-24 pb-16">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="mb-8">
            <div className="text-sm text-gray-500 mb-4">September 15, 2025</div>
            <h1 className="text-4xl font-bold text-black mb-6">
              Metro Detroit Market Update - Q3 2025 & 2026 Outlook
            </h1>
            <img 
              src="https://public.readdy.ai/ai/img_res/ad2e74b345fc648d13bb34f618082338.jpg"
              alt="Metro Detroit Market Q3 2025 Update"
              className="w-full h-64 object-cover object-top rounded-lg mb-8"
            />
          </div>

          <article className="prose prose-lg max-w-none">
            <div className="bg-yellow-50 border-l-4 border-yellow-500 p-6 mb-8">
              <h3 className="text-xl font-bold text-black mb-2">Q3 2025 Key Results</h3>
              <ul className="text-gray-700 space-y-2">
                <li>• Achieved 5.8% home price appreciation year-to-date</li>
                <li>• Inventory increased 22% compared to Q3 2024</li>
                <li>• Average days on market: 28 days (down from 45 in Q1)</li>
                <li>• New construction sales up 35% from last year</li>
              </ul>
            </div>

            <h2 className="text-2xl font-bold text-black mb-4">Q3 2025 Market Performance</h2>
            <p className="text-gray-700 mb-6">
              The third quarter of 2025 has exceeded our early-year projections, with Metro Detroit's real estate market showing remarkable resilience and growth. The tri-county area has experienced stronger-than-expected price appreciation while maintaining healthy inventory levels, creating an ideal environment for both buyers and sellers.
            </p>

            <h2 className="text-2xl font-bold text-black mb-4">Price Performance by County</h2>
            <div className="grid md:grid-cols-3 gap-6 mb-8">
              <div className="bg-white border-2 border-yellow-500 rounded-lg p-6">
                <h3 className="text-xl font-bold text-black mb-3">Wayne County</h3>
                <div className="text-3xl font-bold text-yellow-600 mb-2">+6.2%</div>
                <p className="text-gray-700 text-sm mb-3">Year-over-year appreciation</p>
                <ul className="text-gray-600 text-sm space-y-1">
                  <li>• Median price: $285,000</li>
                  <li>• Average DOM: 25 days</li>
                  <li>• Inventory: +18%</li>
                </ul>
              </div>
              
              <div className="bg-white border-2 border-yellow-500 rounded-lg p-6">
                <h3 className="text-xl font-bold text-black mb-3">Oakland County</h3>
                <div className="text-3xl font-bold text-yellow-600 mb-2">+5.1%</div>
                <p className="text-gray-700 text-sm mb-3">Year-over-year appreciation</p>
                <ul className="text-gray-600 text-sm space-y-1">
                  <li>• Median price: $425,000</li>
                  <li>• Average DOM: 32 days</li>
                  <li>• Inventory: +25%</li>
                </ul>
              </div>
              
              <div className="bg-white border-2 border-yellow-500 rounded-lg p-6">
                <h3 className="text-xl font-bold text-black mb-3">Macomb County</h3>
                <div className="text-3xl font-bold text-yellow-600 mb-2">+6.8%</div>
                <p className="text-gray-700 text-sm mb-3">Year-over-year appreciation</p>
                <ul className="text-gray-600 text-sm space-y-1">
                  <li>• Median price: $315,000</li>
                  <li>• Average DOM: 27 days</li>
                  <li>• Inventory: +28%</li>
                </ul>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-black mb-4">Market Drivers in Q3 2025</h2>
            <p className="text-gray-700 mb-4">
              Several key factors have contributed to the strong Q3 performance:
            </p>
            <ul className="text-gray-700 mb-6 space-y-2">
              <li>• Interest rates stabilized in the 6.2%-6.8% range, improving buyer confidence</li>
              <li>• Major automotive investments brought 8,500 new jobs to the region</li>
              <li>• New construction deliveries increased housing supply significantly</li>
              <li>• Out-of-state buyer migration continued, particularly from California and New York</li>
              <li>• Corporate relocations to downtown Detroit accelerated luxury condo demand</li>
            </ul>

            <h2 className="text-2xl font-bold text-black mb-4">2026 Market Projections</h2>
            
            <div className="bg-gray-50 rounded-lg p-6 mb-8">
              <h3 className="text-xl font-bold text-black mb-4">Expected Trends for 2026</h3>
              <div className="grid md:grid-cols-2 gap-6">
                <div>
                  <h4 className="font-bold text-black mb-3">Price Appreciation</h4>
                  <ul className="text-gray-700 space-y-2">
                    <li>• Projected 3.5%-4.8% appreciation</li>
                    <li>• More sustainable growth pace</li>
                    <li>• Luxury market ($500K+) leading gains</li>
                    <li>• Entry-level homes maintaining strong demand</li>
                  </ul>
                </div>
                <div>
                  <h4 className="font-bold text-black mb-3">Market Conditions</h4>
                  <ul className="text-gray-700 space-y-2">
                    <li>• Inventory levels to stabilize at 4-5 months</li>
                    <li>• Interest rates projected 5.8%-6.5%</li>
                    <li>• New construction to add 18,000 units</li>
                    <li>• Commercial development accelerating</li>
                  </ul>
                </div>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-black mb-4">Emerging Neighborhoods to Watch in 2026</h2>
            <div className="space-y-6 mb-8">
              <div className="bg-white border-2 border-yellow-500 rounded-lg p-6">
                <h3 className="text-xl font-bold text-black mb-3">Ferndale & Royal Oak</h3>
                <p className="text-gray-700 mb-3">
                  Young professional migration driving demand for walkable communities with dining and entertainment options.
                </p>
                <ul className="text-gray-600 text-sm space-y-1">
                  <li>• Projected 2026 appreciation: 6-8%</li>
                  <li>• New mixed-use developments planned</li>
                  <li>• Transit improvements enhancing connectivity</li>
                </ul>
              </div>

              <div className="bg-white border-2 border-yellow-500 rounded-lg p-6">
                <h3 className="text-xl font-bold text-black mb-3">Canton & Plymouth</h3>
                <p className="text-gray-700 mb-3">
                  Family-friendly communities benefiting from excellent schools and new construction activity.
                </p>
                <ul className="text-gray-600 text-sm space-y-1">
                  <li>• Projected 2026 appreciation: 4-6%</li>
                  <li>• Major retail and recreation investments</li>
                  <li>• Strong resale value potential</li>
                </ul>
              </div>

              <div className="bg-white border-2 border-yellow-500 rounded-lg p-6">
                <h3 className="text-xl font-bold text-black mb-3">Rochester Hills & Troy</h3>
                <p className="text-gray-700 mb-3">
                  Luxury market strength continuing with corporate executive relocations and premium amenities.
                </p>
                <ul className="text-gray-600 text-sm space-y-1">
                  <li>• Projected 2026 appreciation: 5-7%</li>
                  <li>• High-end new construction increasing</li>
                  <li>• Technology sector job growth driving demand</li>
                </ul>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-black mb-4">Strategic Opportunities for 2026</h2>
            
            <div className="grid md:grid-cols-2 gap-8 mb-8">
              <div className="bg-white border-2 border-yellow-500 rounded-lg p-6">
                <h3 className="text-xl font-bold text-black mb-4">For Buyers</h3>
                <ul className="text-gray-700 space-y-2">
                  <li>• Q4 2025 presents seasonal buying opportunities</li>
                  <li>• New construction incentives peak in winter months</li>
                  <li>• Rate lock programs becoming more favorable</li>
                  <li>• Inventory selection at 3-year highs</li>
                  <li>• First-time buyer programs expanding in 2026</li>
                </ul>
              </div>
              
              <div className="bg-white border-2 border-yellow-500 rounded-lg p-6">
                <h3 className="text-xl font-bold text-black mb-4">For Sellers</h3>
                <ul className="text-gray-700 space-y-2">
                  <li>• Spring 2026 market expected to be robust</li>
                  <li>• Home improvements showing strong ROI</li>
                  <li>• Energy efficiency upgrades in high demand</li>
                  <li>• Strategic pricing crucial in balanced market</li>
                  <li>• Professional staging more important than ever</li>
                </ul>
              </div>
            </div>

            <h2 className="text-2xl font-bold text-black mb-4">Economic Factors Shaping 2026</h2>
            <p className="text-gray-700 mb-4">
              Several economic developments will influence the 2026 real estate landscape:
            </p>
            <ul className="text-gray-700 mb-6 space-y-2">
              <li>• Ford's $3.7 billion EV investment creating 6,200 new jobs</li>
              <li>• Google's Detroit tech hub expansion adding 2,800 positions</li>
              <li>• Infrastructure improvements enhancing regional connectivity</li>
              <li>• Downtown Detroit residential projects attracting young professionals</li>
              <li>• Tourism and hospitality sector recovery boosting service jobs</li>
            </ul>

            <h2 className="text-2xl font-bold text-black mb-4">Investment Outlook</h2>
            <p className="text-gray-700 mb-6">
              Metro Detroit continues to attract both local and national real estate investors. The combination of affordable entry points, strong rental demand, and economic diversification makes the region particularly attractive for long-term real estate investment strategies. Rental yields remain competitive at 8-12% in many submarkets.
            </p>

            <div className="bg-black text-white rounded-lg p-8 text-center">
              <h3 className="text-2xl font-bold mb-4">Ready to Capitalize on 2026 Opportunities?</h3>
              <p className="text-gray-300 mb-6">
                Whether you're planning to buy, sell, or invest, positioning yourself now for 2026 success starts with expert market guidance.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button 
                  onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                  className="bg-yellow-500 text-black px-8 py-3 rounded-lg hover:bg-yellow-400 transition-colors font-semibold cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-calendar-line mr-2"></i>
                  Schedule 2026 Strategy Session
                </button>
                <button className="bg-transparent border-2 border-white text-white px-8 py-3 rounded-lg hover:bg-white hover:text-black transition-colors font-semibold cursor-pointer whitespace-nowrap">
                  <i className="ri-phone-line mr-2"></i>
                  Call: (248) 248-4801
                </button>
              </div>
            </div>
          </article>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
